import styled from "styled-components";
import { toast } from "react-toastify";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "../Modal/Modal";
import { getAssets } from "../../redux/reducers/assetReducer";
import { CircularProgress, CloudArrowDown } from "../Icons/Icons";
import { Asset, createUserAssetServer } from "../../redux/actions/assetAction";

const ACCEPTED_FORMATS = "audio/mp3, audio/wav, audio/mpeg";
const MAX_SAMPLE_SIZE = 104857600; // 100 MB
const MAX_AUDIO_DURATION = 1; // 1 minute in seconds
const ERROR_MESSAGES = {
  size: "File size exceeds the 100 MB limit.",
  duration: "Please make sure your audio duration is less than 1 minute.",
};
interface Props {
  open: boolean;
  onClose: () => void;
}
const UploadAudioPopup = ({ open, onClose }: Props) => {
  const assets = useSelector(getAssets);
  const uploadSampleLoading = assets[Asset.Sample].uploadLoading;

  const [errorSizeSample, setErrorSizeSample] = useState<boolean>(false);

  const audioInputRef = useRef<HTMLInputElement | null>(null);

  const dispatch = useDispatch();

  const sampleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];

    if (!uploadedFile) return;

    if (uploadedFile.size > MAX_SAMPLE_SIZE) {
      setErrorSizeSample(true);
      toast.error(ERROR_MESSAGES.size);
      return;
    }

    const audio = new Audio();
    audio.addEventListener("loadedmetadata", () => {
      const durationInSeconds = audio.duration;
      const durationInMinutes = Math.floor(durationInSeconds / 60);

      if (durationInMinutes > MAX_AUDIO_DURATION) {
        toast.error(ERROR_MESSAGES.duration);
        if (audioInputRef.current) {
          audioInputRef.current.value = "";
        }
        return;
      } else {
        const formData = new FormData();
        formData.append("AssetTypeId", "4");
        formData.append("Status", "1");
        formData.append("FileName", uploadedFile.name);
        formData.append("File", uploadedFile);

        setErrorSizeSample(false);
        dispatch(
          createUserAssetServer({
            data: formData,
            assetTypeId: Asset.Sample,
          }),
        );
      }
    });

    audio.src = URL.createObjectURL(uploadedFile);
  };
  return (
    <Modal
      title="Upload audio"
      className="uploadAudio"
      open={open}
      onClose={onClose}
      maxWidth={373}
      maxHeight={175}
      closeIcon={false}
    >
      <DragAndDropWrapper error={errorSizeSample}>
        {uploadSampleLoading ? (
          <CircularProgress color="#ffffff" />
        ) : (
          <>
            <input type="file" ref={audioInputRef} multiple accept={ACCEPTED_FORMATS} onChange={sampleUpload} />
            <CloudArrowDown />
            <h3>Drag and drop your voice</h3>
            <SampleFormat error={errorSizeSample}> File Supported: mp3, wav,. Maximum size: 100 MB</SampleFormat>
          </>
        )}
      </DragAndDropWrapper>
    </Modal>
  );
};

const DragAndDropWrapper = styled.div<{ error: boolean }>`
  position: relative;
  margin-top: 12px;
  padding: 20px 12px;
  gap: 8px;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme, error }) => (error ? theme.red : theme.blueDark)};

  & > h3 {
    font-family: "Mulish";
    font-weight: 500;
    font-size: 14px;
    color: ${({ theme }) => theme.primaryText};
  }

  & > input {
    width: 100%;
    height: 100%;
    opacity: 0;
    top: 0;
    left: 0;
    position: absolute;
    cursor: pointer;
  }
`;

const SampleFormat = styled.p<{ error?: boolean }>`
  font-family: "Mulish";
  font-weight: 400;
  font-size: 12px;
  color: ${({ theme, error }) => (error ? theme.red : theme.primaryText)};
  opacity: ${({ error }) => (error ? 1 : 0.4)};
`;

export default UploadAudioPopup;
